/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Divider, Stack, styled } from '@mui/material';
import Typography from './Typography';
import { TestIds } from '../testIds';
import { colors } from '@farmlands/mui-theme';
import { InlineFlex } from '../features/cart/components/CartSummaryComponents';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

interface Props {
  title: string;
  accountName?: string;
  accountNumber?: string;
  total: string;
  gst: string;
  discount?: string;
  delivery?: string;
  deliveryLabel?: string | React.ReactElement;
  showDivider?: boolean;
  backgroundColor?: string;
}

export const OrderSummary = ({
  title,
  accountName,
  accountNumber,
  total,
  gst,
  discount,
  delivery,
  deliveryLabel = 'Delivery cost',
  showDivider,
  backgroundColor,
}: Props) => (
  <Stack
    spacing={2}
    flex={1}
    sx={{
      background: backgroundColor ?? colors.primary50,
    }}
  >
    <Typography
      variant='h6'
      fontSize={'20px'}
      color='primary500'
      fontWeight={700}
      data-testid={TestIds.feat.orders.summary.title}
    >
      {title}
    </Typography>
    <Container>
      <InlineFlex data-testid={TestIds.feat.orders.summary.customerInfo}>
        <Typography variant='body2'>{accountName}</Typography>
        <Typography variant='body2' color={'grey800'} fontWeight={800}>
          ({accountNumber})
        </Typography>
      </InlineFlex>
    </Container>
    {showDivider && <Divider color={colors.primary200} />}
    <Container>
      <InlineFlex data-testid={TestIds.feat.orders.summary.totalCost}>
        <Typography variant='body2' fontWeight={700}>
          Total cost&nbsp;
          <Typography fontSize='12px' component='span' lineHeight={'18px'}>
            (GST incl.)
          </Typography>
        </Typography>
        <Typography variant='body2' fontWeight={700}>
          {total}
        </Typography>
      </InlineFlex>

      {delivery && (
        <InlineFlex data-testid={TestIds.feat.orders.summary.deliveryFee}>
          {typeof deliveryLabel === 'string' ? (
            <Typography variant='body2'>{deliveryLabel}</Typography>
          ) : (
            deliveryLabel
          )}
          <Typography variant='body2'>{delivery}</Typography>
        </InlineFlex>
      )}

      <InlineFlex data-testid={TestIds.feat.orders.summary.gst}>
        <Typography variant='body2'>GST</Typography>
        <Typography variant='body2'>{gst}</Typography>
      </InlineFlex>

      {discount && (
        <InlineFlex data-testid={TestIds.feat.orders.summary.discountCost}>
          <Typography variant='body2' color={'secondary700'}>
            Your Discount&nbsp;
            <Typography color={'secondary700'} fontSize='12px' component='span' lineHeight={'18px'}>
              (GST incl.)
            </Typography>
          </Typography>
          <Typography variant='body2' color={'secondary700'}>
            {discount}
          </Typography>
        </InlineFlex>
      )}
    </Container>
  </Stack>
);
